import React from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Alert,
    Row,
    Col,
    Button,
} from "reactstrap";

const PlanModal = ({
    isOpen,
    toggle,
    formData,
    handleInputChange,
    handleSubmit,
    tokens = [],
    isTokenLoading,
    tokenError,
    isCreating,
    createError,
    isSuccess,
}) => {
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                Create New Staking Plan
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {isTokenLoading && (
                        <Alert color="info">
                            <div
                                className="spinner-border spinner-border-sm mr-2"
                                role="status"
                            >
                                <span className="sr-only">Loading...</span>
                            </div>
                            Loading available tokens...
                        </Alert>
                    )}

                    {tokenError && (
                        <Alert color="danger">
                            Error loading tokens: {tokenError.message}
                        </Alert>
                    )}

                    {createError && (
                        <Alert color="danger" className="mt-3">
                            <strong>Error:</strong>{" "}
                            {createError?.message || "Unknown error occurred"}
                        </Alert>
                    )}

                    {isSuccess && (
                        <Alert color="success" className="mt-3">
                            <strong>Success:</strong> Plan created successfully!
                        </Alert>
                    )}

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="tokenId">Select Token *</Label>
                                <Input
                                    type="select"
                                    name="tokenId"
                                    id="tokenId"
                                    value={formData.tokenId}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isTokenLoading}
                                >
                                    <option value="">Choose a token...</option>
                                    {tokens?.map((token) => (
                                        <option
                                            key={token._id}
                                            value={token._id}
                                        >
                                            {`${token.name} (${token.symbol})`}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="durationInDays">
                                    Duration (Days) *
                                </Label>
                                <Input
                                    type="select"
                                    name="durationInDays"
                                    id="durationInDays"
                                    value={formData.durationInDays}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="30">30 Days</option>
                                    <option value="60">60 Days</option>
                                    <option value="90">90 Days</option>
                                    <option value="180">
                                        180 Days (6 Months)
                                    </option>
                                    <option value="365">
                                        365 Days (1 Year)
                                    </option>
                                    <option value="730">
                                        730 Days (2 Years)
                                    </option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="interestRate">
                                    Interest Rate (% APR) *
                                </Label>
                                <InputGroup>
                                    <Input
                                        type="number"
                                        name="interestRate"
                                        id="interestRate"
                                        placeholder="5.0"
                                        step="0.1"
                                        min="0.1"
                                        max="100"
                                        value={formData.interestRate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    <InputGroupAddon addonType="append">
                                        <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                </InputGroup>
                                <small className="form-text text-muted">
                                    Annual percentage yield (0.1% - 100%)
                                </small>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="minInvestment">
                                    Minimum Stake *
                                </Label>
                                <Input
                                    type="number"
                                    name="minInvestment"
                                    id="minInvestment"
                                    placeholder="100"
                                    step="1"
                                    min="1"
                                    value={formData.minInvestment}
                                    onChange={handleInputChange}
                                    required
                                />
                                <small className="form-text text-muted">
                                    Minimum amount required to participate
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="investmentDeadline">
                                    Staking Deadline *
                                </Label>
                                <Input
                                    type="date"
                                    name="investmentDeadline"
                                    id="investmentDeadline"
                                    value={formData.investmentDeadline}
                                    onChange={handleInputChange}
                                    min={new Date().toISOString().split("T")[0]}
                                    required
                                />
                                <small className="form-text text-muted">
                                    Last date to join this Staking plan
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup check>
                                <Input
                                    type="checkbox"
                                    name="isActive"
                                    id="isActive"
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                />
                                <Label check for="isActive">
                                    <strong>Activate plan immediately</strong>
                                </Label>
                                <br />
                                <small className="form-text text-muted">
                                    Uncheck to create the plan as inactive (can
                                    be activated later)
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr className="my-3" />

                    <div className="bg-secondary p-3 rounded">
                        <h6 className="text-muted mb-3">Plan Summary</h6>
                        <Row>
                            <Col md="6">
                                <small className="text-muted">Duration:</small>
                                <div className="font-weight-bold">
                                    {formData.durationInDays} days
                                    {formData.durationInDays >= 365 && (
                                        <span className="text-muted">
                                            {" "}
                                            (
                                            {Math.round(
                                                (formData.durationInDays /
                                                    365) *
                                                    10
                                            ) / 10}{" "}
                                            years)
                                        </span>
                                    )}
                                </div>
                            </Col>
                            <Col md="6">
                                <small className="text-muted">
                                    Expected Return:
                                </small>
                                <div className="font-weight-bold text-success">
                                    {formData.interestRate}% APR
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md="6">
                                <small className="text-muted">
                                    Min Stake:
                                </small>
                                <div className="font-weight-bold">
                                    {formData.minInvestment.toLocaleString()}
                                </div>
                            </Col>
                            {/* <Col md="6">
                                <small className="text-muted">
                                    Example Return:
                                </small>
                                <div className="font-weight-bold text-info">
                                    {Math.round(
                                        formData.minInvestment *
                                            (formData.interestRate / 100) *
                                            (formData.durationInDays / 365)
                                    ).toLocaleString()}
                                    <small className="text-muted">
                                        {" "}
                                        (on min investment)
                                    </small>
                                </div>
                            </Col> */}
                        </Row>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        type="submit"
                        disabled={isCreating || isTokenLoading}
                    >
                        {isCreating ? (
                            <>
                                <div
                                    className="spinner-border spinner-border-sm mr-2"
                                    role="status"
                                >
                                    <span className="sr-only">Loading...</span>
                                </div>
                                Creating Plan...
                            </>
                        ) : (
                            "Create Staking Plan"
                        )}
                    </Button>{" "}
                    <Button
                        color="secondary"
                        onClick={toggle}
                        disabled={isCreating}
                    >
                        Cancel
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default PlanModal;
